import { useEffect } from "react";
import nprogress from "nprogress";
import "nprogress/nprogress.css";
import { useState } from "react";
import {
  Outlet,
  redirect,
  useNavigation,
  useLocation,
} from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Header from "@/components/blocks/Header/Header";
import { SidebarContent } from "@/components/blocks/SidebarContent/SidebarContent";
import { cn } from "@/lib/utils";
import { fetchUserProfile } from "@/hooks/useUser";
import { queryClient } from "@/lib/queryClient";

export async function protectedLoader() {
  try {
    const data = await queryClient.fetchQuery({
      queryKey: ["authUser"],
      queryFn: fetchUserProfile,
      staleTime: 1000 * 60 * 5,
    });

    return data;
  } catch {
    return redirect("/login");
  }
}

export default function Layout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigation = useNavigation();
  const location = useLocation();

  useEffect(() => {
    if (navigation.state === "loading") {
      nprogress.start();
    } else {
      nprogress.done();
    }

    return () => {
      nprogress.done();
    };
  }, [navigation.state]);

  return (
    <div className="flex min-h-screen bg-background font-sans antialiased">
      {/* Desktop Sidebar */}
      <aside className="dark fixed z-30 hidden h-full w-18 flex-col border-r border-sidebar-border bg-sidebar shadow-none min-[1100px]:flex">
        <SidebarContent onClose={() => setIsSidebarOpen(false)} />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground/45 backdrop-blur-sm transition-opacity duration-300 min-[1100px]:hidden motion-reduce:backdrop-blur-none"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={cn(
          "dark fixed inset-y-0 left-0 z-50 flex h-full w-18 flex-col border-r border-sidebar-border bg-sidebar shadow-none transition-transform duration-300 ease-in-out min-[1100px]:hidden",
          isSidebarOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <SidebarContent onClose={() => setIsSidebarOpen(false)} />
      </aside>

      <main className="ml-0 flex min-h-screen min-w-0 flex-1 flex-col bg-background transition-all duration-300 min-[1100px]:ml-18">
        {/* === PAGE CONTENT === */}
        <Header setIsSidebarOpen={setIsSidebarOpen} />
        <div className="w-full flex-1 overflow-x-clip p-4 md:p-8">
          <div className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-500 motion-safe:ease-out">
            <ErrorBoundary key={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </div>
      </main>
    </div>
  );
}
